import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Chart from 'react-apexcharts';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { useGetLoanBookSummary } from '../../../services/loanService';
import type { LoanBookSummary as LoanBookSummaryData, MonthlyLoanTrend, RecentLoanActivity } from '../../../services/schemas/loanServiceSchemas';
import './stats-cards.css';

const LoanBookSummary = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const navigate = useNavigate();

  // Format the month as YYYY-MM-DD (first day of the month)
  const targetMonth = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}-01`;

  // Use the real API hook
  const { data: summaryData, isLoading, error } = useGetLoanBookSummary({ targetMonth });

  // Handle errors
  useEffect(() => {
    if (error) {
      console.error('Error fetching loan book summary data:', error);
      toast.error('Failed to fetch loan book summary data');
    }
  }, [error]);

  // Extract data from API response
  const summary = summaryData || {};
  const monthlyTrends = summary.monthlyTrends || [];
  const recentActivity = summary.recentActivity || [];

  // Handle month change
  const handleMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(event.target.value + '-01');
    setSelectedMonth(newDate);
  };

  // Format selected month for display
  const formatSelectedMonth = (date: Date) => {
    return date.toLocaleDateString('en', { month: 'long', year: 'numeric' });
  };

  // Get activity icon and color
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'payment':
        return { icon: 'fa-check-circle', color: 'text-success' };
      case 'disbursement':
        return { icon: 'fa-money', color: 'text-primary' };
      case 'overdue':
        return { icon: 'fa-exclamation-triangle', color: 'text-danger' };
      case 'new_loan':
        return { icon: 'fa-plus-circle', color: 'text-info' };
      default:
        return { icon: 'fa-circle', color: 'text-secondary' };
    }
  };

  // Chart configurations
  const monthlyTrendsChart = {
    series: [{
      name: 'Disbursed',
      data: monthlyTrends.map(trend => trend.disbursed || 0)
    }, {
      name: 'Collected',
      data: monthlyTrends.map(trend => trend.collected || 0)
    }, {
      name: 'Outstanding',
      data: monthlyTrends.map(trend => trend.outstanding || 0)
    }],
    options: {
      chart: {
        type: 'bar' as const,
        height: 350,
        toolbar: { show: false },
        background: 'transparent'
      },
      colors: ['#0d6efd', '#28a745', '#dc3545'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 8,
        }
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: monthlyTrends.map(trend => trend.month),
        labels: {
          style: {
            fontSize: '14px',
            fontFamily: 'inherit'
          }
        }
      },
      yaxis: {
        labels: {
          formatter: function (val: number) {
            return 'R' + (val / 1000000).toFixed(1) + 'M';
          },
          style: {
            fontSize: '14px',
            fontFamily: 'inherit'
          }
        }
      },
      fill: { opacity: 1 },
      tooltip: {
        theme: 'light',
        style: {
          fontSize: '14px',
          fontFamily: 'inherit'
        },
        custom: function({ series, seriesIndex, dataPointIndex, w }: any) {
          const value = series[seriesIndex][dataPointIndex];
          const seriesName = w.globals.seriesNames[seriesIndex];
          return `
            <div style="
              background: white;
              border: 1px solid #e9ecef;
              border-radius: 8px;
              padding: 12px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
              font-family: inherit;
            ">
              <div style="
                font-weight: 600;
                color: #333;
                margin-bottom: 4px;
                font-size: 14px;
              ">${seriesName}</div>
              <div style="
                font-weight: bold;
                color: #1a1a1a;
                font-size: 16px;
              ">${formatCurrency(value)}</div>
            </div>
          `;
        }
      },
      legend: {
        position: 'top' as const,
        horizontalAlign: 'center' as const,
        fontSize: '14px',
        fontFamily: 'inherit',
        markers: {
          size: 6
        }
      }
    }
  };

  const portfolioCompositionChart = {
    series: [summary.activeLoans || 0, summary.paidLoans || 0, summary.overdueLoans || 0, summary.partialLoans || 0, summary.defaultLoans || 0],
    options: {
      chart: {
        type: 'donut' as const,
        height: 300,
        background: 'transparent'
      },
      colors: ['#0d6efd', '#28a745', '#dc3545', '#ffc107', '#6c757d'],
      labels: ['Active', 'Paid', 'Overdue', 'Partial', 'Default'],
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '16px',
                fontWeight: 600,
                color: '#333333',
                offsetY: -10
              },
              value: {
                show: true,
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#1a1a1a',
                offsetY: 10,
                formatter: function (val: string) {
                  return parseInt(val) + ' loans';
                }
              },
              total: {
                show: true,
                label: 'Total Loans',
                fontSize: '14px',
                fontWeight: 600,
                color: '#666666',
                formatter: function (w: any) {
                  const total = w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                  return total + ' loans';
                }
              }
            }
          }
        }
      },
      legend: {
        position: 'bottom' as const,
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'inherit',
        labels: {
          colors: ['#333333'],
          useSeriesColors: false
        },
        markers: {
          size: 6
        }
      },
      tooltip: {
        theme: 'light',
        style: {
          fontSize: '14px',
          fontFamily: 'inherit'
        }
      },
      dataLabels: {
        enabled: false
      }
    }
  };

  const securityChart = {
    series: [summary.securedLoans || 0, summary.unsecuredLoans || 0],
    options: {
      chart: {
        type: 'donut' as const,
        height: 250,
        background: 'transparent'
      },
      colors: ['#28a745', '#dc3545'],
      labels: ['Secured', 'Unsecured'],
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '14px',
                fontWeight: 600,
                color: '#333333',
                offsetY: -10
              },
              value: {
                show: true,
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#1a1a1a',
                offsetY: 10,
                formatter: function (val: string) {
                  return parseInt(val) + ' loans';
                }
              }
            }
          }
        }
      },
      legend: {
        position: 'bottom' as const,
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'inherit'
      },
      dataLabels: {
        enabled: false
      }
    }
  };

  if (isLoading) {
    return (
      <>
        <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
          <div className="container-fluid">
            <div className="row align-items-center">
              <div className="col-md-8">
                <h1 className="fs-2 fw-bold mb-2 text-white">
                  Loan Book Summary 📊
                </h1>
                <p className="mb-0 text-white fs-6 opacity-75">
                  Loading loan portfolio analytics for {formatSelectedMonth(selectedMonth)}...
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="page-body px-xl-4 px-sm-2 px-0 py-4 bg-body">
          <div className="container-fluid">
            <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
              <div className="text-center">
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading loan portfolio analytics...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/">
                    <i className="fa fa-home me-2"></i>Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/loan-book">Loan Book</Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">Summary</li>
              </ol>
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="fs-2 fw-bold mb-2 text-white">
                Loan Book Summary 📊
              </h1>
              <p className="mb-0 text-white fs-6 opacity-75">
                Portfolio analytics for {formatSelectedMonth(selectedMonth)}
              </p>
            </div>
            <div className="col-md-6 text-md-end">
              <div className="d-flex gap-3 justify-content-md-end align-items-center">
                <div className="d-flex align-items-center bg-white text-dark rounded-pill px-4 py-2" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                  <i className="fa fa-calendar me-2 text-primary"></i>
                  <input
                    type="month"
                    className="border-0 bg-transparent fw-medium"
                    style={{ outline: 'none', minWidth: '120px' }}
                    value={`${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`}
                    onChange={handleMonthChange}
                  />
                </div>
                <div className="d-inline-flex align-items-center bg-white text-dark rounded-pill px-4 py-2" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                  <i className="fa fa-clock-o me-2 text-success"></i>
                  <span className="fw-medium">{new Date().toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-4 bg-body">
        <div className="container-fluid">

          {/* Key Metrics Cards */}
          <div className="row g-4 mb-5">
            <div className="col-xl-3 col-lg-6">
              <div className="card h-100 bg-white position-relative overflow-hidden border-start border-5 border-primary"
                   style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
                <div className="card-body p-4">
                  <div className="row align-items-center">
                    <div className="col">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-light-primary rounded-3 p-3 me-3">
                          <i className="fa fa-book fs-3 text-primary"></i>
                        </div>
                        <div>
                          <h6 className="card-title mb-0 text-dark fw-semibold">Total Loans</h6>
                          <small className="text-muted">Active portfolio</small>
                        </div>
                      </div>
                      <div className="d-flex align-items-baseline justify-content-between">
                        <h3 className="fw-bold mb-0 text-dark">{summary.totalLoans || 0}</h3>
                        <span className="badge bg-light-primary text-primary fs-6 fw-semibold">
                          ↗
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="position-absolute top-0 end-0" style={{ opacity: 0.05, fontSize: '4rem', margin: '1rem' }}>
                  <i className="fa fa-book text-primary"></i>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-6">
              <div className="card h-100 bg-white position-relative overflow-hidden border-start border-5 border-success"
                   style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
                <div className="card-body p-4">
                  <div className="row align-items-center">
                    <div className="col">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-light-success rounded-3 p-3 me-3">
                          <i className="fa fa-money fs-3 text-success"></i>
                        </div>
                        <div>
                          <h6 className="card-title mb-0 text-dark fw-semibold">Total Portfolio</h6>
                          <small className="text-muted">Total loan amounts</small>
                        </div>
                      </div>
                      <div className="d-flex align-items-baseline justify-content-between">
                        <h3 className="fw-bold mb-0 text-dark fs-6">{formatCurrency(summary.totalBalance || 0)}</h3>
                        <span className="badge bg-light-success text-success fs-6 fw-semibold">
                          100%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="position-absolute top-0 end-0" style={{ opacity: 0.05, fontSize: '4rem', margin: '1rem' }}>
                  <i className="fa fa-money text-success"></i>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-6">
              <div className="card h-100 bg-white position-relative overflow-hidden border-start border-5 border-info"
                   style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
                <div className="card-body p-4">
                  <div className="row align-items-center">
                    <div className="col">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-light-info rounded-3 p-3 me-3">
                          <i className="fa fa-credit-card fs-3 text-info"></i>
                        </div>
                        <div>
                          <h6 className="card-title mb-0 text-dark fw-semibold">Total Charges</h6>
                          <small className="text-muted">Charged interests + admin fees</small>
                        </div>
                      </div>
                      <div className="d-flex align-items-baseline justify-content-between">
                        <h3 className="fw-bold mb-0 text-dark fs-6">{formatCurrency(summary.totalCharges || 0)}</h3>
                        <span className="badge bg-light-info text-info fs-6 fw-semibold">
                          💰
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="position-absolute top-0 end-0" style={{ opacity: 0.05, fontSize: '4rem', margin: '1rem' }}>
                  <i className="fa fa-credit-card text-info"></i>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-6">
              <div className="card h-100 bg-white position-relative overflow-hidden border-start border-5 border-warning"
                   style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
                <div className="card-body p-4">
                  <div className="row align-items-center">
                    <div className="col">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-light-warning rounded-3 p-3 me-3">
                          <i className="fa fa-exclamation-triangle fs-3 text-warning"></i>
                        </div>
                        <div>
                          <h6 className="card-title mb-0 text-dark fw-semibold">Outstanding</h6>
                          <small className="text-muted">Remaining balance</small>
                        </div>
                      </div>
                      <div className="d-flex align-items-baseline justify-content-between">
                        <h3 className="fw-bold mb-0 text-dark fs-6">{formatCurrency(summary.totalRemainingAmount || 0)}</h3>
                        <span className="badge bg-light-warning text-warning fs-6 fw-semibold">
                          {summary.overdueLoans || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="position-absolute top-0 end-0" style={{ opacity: 0.05, fontSize: '4rem', margin: '1rem' }}>
                  <i className="fa fa-exclamation-triangle text-warning"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="row g-4 mb-5">
            <div className="col-xl-8">
              <div className="card border-0 shadow-sm h-100 bg-card">
                <div className="card-header bg-transparent border-0 pb-0">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="bg-light-primary rounded-3 p-3 me-3">
                        <i className="fa fa-bar-chart text-primary fs-4"></i>
                      </div>
                      <div>
                        <h5 className="card-title mb-0">Monthly Portfolio Trends</h5>
                        <p className="text-muted mb-0 small">Disbursed vs Collected vs Outstanding</p>
                      </div>
                    </div>
                    <div className="badge bg-light-primary text-primary px-3 py-2 rounded-pill">
                      <i className="fa fa-calendar me-1"></i>
                      Last 6 Months
                    </div>
                  </div>
                </div>
                <div className="card-body pt-2">
                  <Chart
                    options={monthlyTrendsChart.options}
                    series={monthlyTrendsChart.series}
                    type="bar"
                    height={350}
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-4">
              <div className="card border-0 shadow-sm h-100 bg-card">
                <div className="card-header bg-transparent border-0 pb-0">
                  <div className="d-flex align-items-center">
                    <div className="bg-light-success rounded-3 p-3 me-3">
                      <i className="fa fa-shield text-success fs-4"></i>
                    </div>
                    <div>
                      <h5 className="card-title mb-0">Security Coverage</h5>
                      <p className="text-muted mb-0 small">Secured vs Unsecured loans</p>
                    </div>
                  </div>
                </div>
                <div className="card-body text-center pt-2">
                  <Chart
                    options={securityChart.options}
                    series={securityChart.series}
                    type="donut"
                    height={250}
                  />
                  <div className="mt-3 p-3 bg-light rounded-3">
                    <div className="row text-center">
                      <div className="col-6">
                        <h6 className="text-muted mb-1">Security Ratio</h6>
                        <p className="mb-0">
                          <span className="text-success fw-bold">{summary.securedLoans || 0}</span>
                          <span className="text-muted"> of </span>
                          <span className="fw-bold">{summary.totalLoans || 0}</span>
                        </p>
                      </div>
                      <div className="col-6">
                        <h6 className="text-muted mb-1">Security Value</h6>
                        <p className="mb-0">
                          <span className="text-info fw-bold">{formatCurrency(summary.totalSecurityValue || 0)}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Portfolio Status Distribution & Recent Activity */}
          <div className="row g-4 mb-5">
            <div className="col-xl-6">
              <div className="card border-0 shadow-sm h-100 bg-card">
                <div className="card-header bg-transparent border-0 pb-0">
                  <div className="d-flex align-items-center">
                    <div className="bg-light-warning rounded-3 p-3 me-3">
                      <i className="fa fa-pie-chart text-warning fs-4"></i>
                    </div>
                    <div>
                      <h5 className="card-title mb-0">Portfolio Composition</h5>
                      <p className="text-muted mb-0 small">Loans by status</p>
                    </div>
                  </div>
                </div>
                <div className="card-body pt-2">
                  <Chart
                    options={portfolioCompositionChart.options}
                    series={portfolioCompositionChart.series}
                    type="donut"
                    height={300}
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-6">
              <div className="card border-0 shadow-sm h-100 bg-card">
                <div className="card-header bg-transparent border-0 pb-0">
                  <div className="d-flex align-items-center">
                    <div className="bg-light-info rounded-3 p-3 me-3">
                      <i className="fa fa-clock-o text-info fs-4"></i>
                    </div>
                    <div>
                      <h5 className="card-title mb-0">Recent Activity</h5>
                      <p className="text-muted mb-0 small">Latest loan activities</p>
                    </div>
                  </div>
                </div>
                <div className="card-body pt-2">
                  <div className="list-group list-group-flush">
                    {recentActivity.map((activity) => {
                      const { icon, color } = getActivityIcon(activity.type || 'payment');
                      return (
                        <div key={activity.id} className="list-group-item px-0 border-0">
                          <div className="d-flex align-items-center">
                            <div className={`me-3 ${color}`}>
                              <i className={`fa ${icon} fs-5`}></i>
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1 text-truncate">{activity.customerName || 'Unknown Customer'}</h6>
                              <p className="mb-0">
                                <span className="fw-semibold">{formatCurrency(activity.amount || 0)}</span>
                                <small className="text-muted ms-2">{formatDate(activity.date || '')}</small>
                              </p>
                              <small className="text-muted">{activity.loanRef || ''}</small>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4">
                    <button
                      className="btn btn-outline-primary w-100"
                      onClick={() => navigate('/loan-book/invoices')}
                    >
                      <i className="fa fa-list me-2"></i>
                      View Full Portfolio
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm bg-card">
                <div className="card-header bg-transparent border-0">
                  <div className="d-flex align-items-center">
                    <div className="bg-light-primary rounded-3 p-3 me-3">
                      <i className="fa fa-bolt text-primary fs-4"></i>
                    </div>
                    <div>
                      <h5 className="card-title mb-0">Quick Actions</h5>
                      <p className="text-muted mb-0 small">Common portfolio management tasks</p>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <button
                        className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center py-3 quick-action-card"
                        onClick={() => navigate('/loan-book/overview')}
                      >
                        <i className="fa fa-book me-2"></i>
                        View Portfolio
                        <i className="fa fa-arrow-right ms-auto"></i>
                      </button>
                    </div>
                    <div className="col-md-3">
                      <button
                        className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center py-3 quick-action-card"
                        onClick={() => navigate('/loan-application/new')}
                      >
                        <i className="fa fa-plus-circle me-2"></i>
                        New Loan
                        <i className="fa fa-arrow-right ms-auto"></i>
                      </button>
                    </div>
                    <div className="col-md-3">
                      <button
                        className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center py-3 quick-action-card"
                        onClick={() => navigate('/loan-book/overview?status=Overdue')}
                      >
                        <i className="fa fa-exclamation-triangle me-2"></i>
                        Overdue Loans
                        <i className="fa fa-arrow-right ms-auto"></i>
                      </button>
                    </div>
                    <div className="col-md-3">
                      <button
                        className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center py-3 quick-action-card"
                        onClick={() => navigate('/reports?category=Loans')}
                      >
                        <i className="fa fa-bar-chart me-2"></i>
                        Loan Reports
                        <i className="fa fa-arrow-right ms-auto"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .quick-action-card {
          transition: all 0.3s ease;
          cursor: pointer;
          border: 2px solid transparent !important;
        }
        .quick-action-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 15px 35px rgba(0,0,0,0.1) !important;
          border-color: #0d6efd !important;
        }
        .quick-action-card:hover .fa-arrow-right {
          transform: translateX(5px);
          transition: transform 0.3s ease;
        }
      `}</style>
    </>
  );
};

export default LoanBookSummary;
export { LoanBookSummary };
